<script setup lang="ts">
import { tv, type VariantProps } from 'tailwind-variants/lite'
import { computed } from 'vue'
import { useToast } from '../../composables'
import LToast from './Toast.vue'

const style = tv({
  base: 'lui-toast-provider',
  variants: {
    position: {
      'top-left': 'lui-toast-provider--position-top-left',
      'top-center': 'lui-toast-provider--position-top-center',
      'top-right': 'lui-toast-provider--position-top-right',
      'bottom-left': 'lui-toast-provider--position-bottom-left',
      'bottom-center': 'lui-toast-provider--position-bottom-center',
      'bottom-right': 'lui-toast-provider--position-bottom-right',
    },
  },
})

export interface ToastProviderProps {
  position?: VariantProps<typeof style>['position']
}

const props = withDefaults(defineProps<ToastProviderProps>(), {
  position: 'bottom-center',
})

const { toasts, remove } = useToast()

const classes = computed(() =>
  style({
    position: props.position,
  }),
)

const transitionName = computed(() => {
  return props.position?.startsWith('top') ? 'lui-toast-top' : 'lui-toast-bottom'
})

function handleActionClick(id: string) {
  remove(id)
}

function handleClose(id: string) {
  remove(id)
}
</script>

<template>
  <Teleport to="body">
    <div :class="classes" role="region" aria-label="Notifications">
      <TransitionGroup :name="transitionName">
        <LToast
          v-for="toast in toasts"
          :key="toast.id"
          :text="toast.text"
          :variant="toast.variant"
          :action="toast.action"
          :closable="toast.closable"
          @click:action="handleActionClick(toast.id)"
          @close="handleClose(toast.id)"
        />
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style>
@reference "../../css/tailwind.css";

@layer components {
  .lui-toast-provider {
    @apply pointer-events-none fixed z-60 flex flex-col;
  }

  .lui-toast-provider--position-top-left {
    @apply top-0 left-0;
  }

  .lui-toast-provider--position-top-center {
    @apply top-0 right-0 left-0 items-center;
  }

  .lui-toast-provider--position-top-right {
    @apply top-0 right-0 items-end;
  }

  .lui-toast-provider--position-bottom-left {
    @apply bottom-0 left-0;
  }

  .lui-toast-provider--position-bottom-center {
    @apply right-0 bottom-0 left-0 items-center;
  }

  .lui-toast-provider--position-bottom-right {
    @apply right-0 bottom-0 items-end;
  }

  .lui-toast-provider .lui-toast {
    @apply pointer-events-auto;
  }

  /* Transition animations - bottom positions */
  .lui-toast-bottom-enter-active,
  .lui-toast-bottom-leave-active {
    @apply transition-all duration-300 ease-out;
  }

  .lui-toast-bottom-enter-from {
    @apply translate-y-4 opacity-0;
  }

  .lui-toast-bottom-leave-to {
    @apply scale-95 opacity-0;
  }

  .lui-toast-bottom-move {
    @apply transition-transform duration-300 ease-out;
  }

  /* Transition animations - top positions */
  .lui-toast-top-enter-active,
  .lui-toast-top-leave-active {
    @apply transition-all duration-300 ease-out;
  }

  .lui-toast-top-enter-from {
    @apply -translate-y-4 opacity-0;
  }

  .lui-toast-top-leave-to {
    @apply scale-95 opacity-0;
  }

  .lui-toast-top-move {
    @apply transition-transform duration-300 ease-out;
  }
}
</style>
